/**
 * Robust date formatter supporting:
 * - Excel serial date numbers (e.g. 45658 -> "01 Jan 2025")
 * - Unix timestamps in ms / seconds
 * - ISO date strings / standard date strings (e.g. "2025-01-01" -> "01 Jan 2025")
 */
export function formatDisplayDate(val) {
  if (!val || val === "—" || val === "null" || val === "undefined") return "—";

  // Check for Excel serial number or numeric timestamp
  const num = Number(val);
  if (!Number.isNaN(num) && num > 0) {
    // Excel serial dates typically range from ~10000 (year 1927) to 80000 (year 2119)
    if (num > 1000 && num < 100000) {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30));
      const dateObj = new Date(excelEpoch.getTime() + num * 86400000);
      if (!Number.isNaN(dateObj.getTime())) {
        const day = String(dateObj.getUTCDate()).padStart(2, "0");
        const month = dateObj.toLocaleString("en-GB", { month: "short", timeZone: "UTC" });
        const year = dateObj.getUTCFullYear();
        return `${day} ${month} ${year}`;
      }
    }

    // Standard Unix timestamp
    const timestampDate = num > 1e11 ? new Date(num) : new Date(num * 1000);
    if (!Number.isNaN(timestampDate.getTime())) {
      const day = String(timestampDate.getDate()).padStart(2, "0");
      const month = timestampDate.toLocaleString("en-GB", { month: "short" });
      const year = timestampDate.getFullYear();
      return `${day} ${month} ${year}`;
    }
  }

  // Check for string date
  const parsed = new Date(val);
  if (!Number.isNaN(parsed.getTime()) && !/^\d+$/.test(String(val).trim())) {
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = parsed.toLocaleString("en-GB", { month: "short" });
    const year = parsed.getFullYear();
    return `${day} ${month} ${year}`;
  }

  return String(val);
}

/**
 * Format a number / numeric string into Indian currency comma format (e.g. 10000 -> "10,000", 100000 -> "1,00,000")
 * Specially designed for Ant Design InputNumber formatter.
 */
export function amountFormatter(value) {
  if (value === undefined || value === null || value === "") return "";
  const str = String(value).replace(/[^0-9.]/g, "");
  if (!str) return "";
  const parts = str.split(".");
  const integerPart = parts[0];
  if (integerPart.length <= 3) {
    return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
  }
  const lastThree = integerPart.substring(integerPart.length - 3);
  let otherNumbers = integerPart.substring(0, integerPart.length - 3);
  let formattedOther = "";
  while (otherNumbers.length > 2) {
    formattedOther = "," + otherNumbers.substring(otherNumbers.length - 2) + formattedOther;
    otherNumbers = otherNumbers.substring(0, otherNumbers.length - 2);
  }
  formattedOther = otherNumbers + formattedOther;
  const formattedInt = formattedOther + "," + lastThree;
  return parts.length > 1 ? `${formattedInt}.${parts[1]}` : formattedInt;
}

/**
 * Strips commas and non-numeric characters (except single decimal point) for Ant Design InputNumber parser.
 */
export function amountParser(value) {
  if (!value) return "";
  return String(value).replace(/\$\s?|(,*)/g, "");
}

/**
 * Format raw text input value with commas while typing
 */
export function formatAmountWithCommas(value) {
  if (value === undefined || value === null || value === "") return "";
  const cleaned = String(value).replace(/[^0-9.]/g, "");
  return amountFormatter(cleaned);
}
